import {
  MarketStatus,
  OrderSide,
  OrderType,
} from '@katanaperps/katana-perps-sdk';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { ApiMarket } from 'api/types';
import BalanceStatsPanelWithData from 'components/balances/BalanceStatsPanelWithData';
import DepositWithdrawalButtons from 'components/balances/DepositWithdrawalButtons';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import { getOrderbookSectionHeight } from 'components/orders/orderBook/orderBookRowUtils';
import TPSLModal from 'components/positions/takeProfitStopLoss/TPSLModal';
import VaultStatsPanel from 'components/vaults/components/VaultStatsPanel';
import { useOwnVaultQuery } from 'components/vaults/shared/utils';
import { HIGH_SPREAD_MARKET_ORDER_PERCENTAGE_LIMIT } from 'config/constants';
import { buildMarketPair } from 'lib/display/formatting';
import { useSelector } from 'reduxState/store';

import BuyFields from './BuyFields';
import { IAction, PanelState } from './buyPanelReducer';
import {
  BuySellPanelInfoBottom,
  BuySellPanelCalculatedInfo,
  MobilePanelInfo,
  TakerMakerFeesTwoColumnData,
} from './buySellInfo';
import BuySellPanelButton from './BuySellPanelButton';
import LeverageFieldsWithModal from './leverage/LeverageFieldsWithModal';
import PanelHeader from './PanelHeader';
import { BuyPanelTopWarning } from './warnings/BuyPanelTopWarning';
import HighSpreadWarningModal from './warnings/HighSpreadWarningModal';

const Wrap = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  /* TODO: not sure why but it's needed :o */
  overflow: hidden;

  .isDesktopStyle & {
    justify-content: flex-start;
  }
  .isMobileStyle & {
    background: var(--elevation-light);
    border-radius: 2px;
  }

  /* background: var(--elevation-light);
  margin: 4px;
  border-radius: 2px; */

  /* .isDesktopStyle & {
    overflow-y: auto;
    $ { scrollbarStyle}
  } */
`;

const InnerWrapper = styled.div<{ $minHeight: number }>`
  transition: all 0.5s ease;
  padding: 0 4px;
  width: 100%;
  position: relative;
  .isDesktopStyle & {
    background: var(--elevation-light);
    border-radius: 2px;
    margin-bottom: 4px;
    padding: 8px;
    min-height: ${({ $minHeight }) => $minHeight}px;
  }

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MobileLeverageAndTypeBlock = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
`;

const FooterWrapper = styled.div`
  flex: 1;
  background: var(--elevation-light);
  border-radius: 2px;
`;

const BalanceActionsWrap = styled.div`
  margin: 0 12px;
  padding: 12px 0;
`;

const LoadingOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: black;

  transition: opacity 1s;
  opacity: ${({ $isVisible }) => ($isVisible ? 0.4 : 0)};
  z-index: ${({ $isVisible }) => ($isVisible ? 123 : -1)};
`;

type Props = {
  dispatch: (action: IAction) => void;
  state: PanelState;
  calculatedInfo: BuySellPanelCalculatedInfo;
  performOrder: (side: OrderSide) => Promise<unknown>;
  token1base: string;
  token2quote: string;
  getBestPrice: () => string;
  getSpreadPercentage: () => number | null;
  priceDecimals: number;
  quantityDecimals: number;
  quantityBelowMinimum: boolean | undefined;
  hasDustPosition: boolean;
  buyEnabled: boolean;
  sellEnabled: boolean;
  marketData?: ApiMarket;
  buyingPower: number | undefined;
  initialMarginFractionOverride: string | undefined | null;
  walletMakerFee: string | undefined;
  walletTakerFee: string | undefined;
};

/**
 * Buy sell panel for Base/Quote token pair (eg. IDEX/ETH)
 */
function BuySellPanel({
  dispatch,
  state,
  calculatedInfo,
  performOrder,
  token1base,
  token2quote,
  getBestPrice,
  getSpreadPercentage,
  buyEnabled,
  sellEnabled,
  priceDecimals,
  quantityDecimals,
  quantityBelowMinimum,
  hasDustPosition,
  marketData,
  buyingPower,
  initialMarginFractionOverride,
  walletMakerFee,
  walletTakerFee,
}: Props) {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const ownVault = useOwnVaultQuery();
  const defaultMakerFeeRate = useSelector(
    state => state.exchange.info.defaultMakerFeeRate
  );
  const defaultTakerFeeRate = useSelector(
    state => state.exchange.info.defaultTakerFeeRate
  );
  const isMobile = useSelector(state => state.screen.isMobile);
  const verticalResolutionIndex = useSelector(
    state => state.screen.verticalResolutionIndex
  );
  const closedLargeSpreadMarketOrderWarning = useSelector(
    state => state.exchange.userGuide.closedLargeSpreadMarketOrderWarning
  );
  const isRestrictedRegion = !!useSelector(
    state => state.region.restrictedRegion
  );
  const [highSpreadConfirm, setHighSpreadConfirm] = useState<{
    side: OrderSide;
    spread: number;
  } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmitCheck = (event: React.FormEvent<HTMLFormElement>) => {
    // Run HTML5 form input checks (html5 validation)
    event.preventDefault();
  };

  const handlePlaceOrder = (side: OrderSide, highSpreadConfirmed?: true) => {
    if (!formRef.current) {
      console.error('buysell - No formRef');
      return;
    }
    const isValid = formRef.current.reportValidity();
    if (!isValid) {
      // HTML should highlight problems for
      console.log('Form not valid');
      return;
    }
    // For market orders with high spread, we want to inform user first
    if (closedLargeSpreadMarketOrderWarning) {
      // Warning already closed with "Do not show again"
    } else if (highSpreadConfirmed === true) {
      setHighSpreadConfirm(null);
    } else if (state.orderType === OrderType.market) {
      const spread = getSpreadPercentage();
      if (
        spread !== null &&
        spread > HIGH_SPREAD_MARKET_ORDER_PERCENTAGE_LIMIT
      ) {
        setHighSpreadConfirm({ side, spread });
        return;
      }
    }
    performOrder(side).catch(error => {
      console.error(error);
      dispatch({ type: 'error', message: error.message });
    });
  };

  const isLoading =
    state.state === 'loadingBuy' || state.state === 'loadingSell';

  const marketSymbol = buildMarketPair(token1base, token2quote);

  const determineFee = (
    defaultRate?: string,
    marketRate?: string,
    walletRate?: string
  ) =>
    !defaultRate || !marketRate || (walletAddress && !walletRate)
      ? undefined // Initializing
      : walletAddress
      ? Math.min(Number(defaultRate), Number(marketRate), Number(walletRate)) *
        100
      : Math.min(Number(defaultRate), Number(marketRate)) * 100;

  const takerFee = determineFee(
    defaultTakerFeeRate,
    marketData?.takerFeeRate,
    walletTakerFee
  );
  const makerFee = determineFee(
    defaultMakerFeeRate,
    marketData?.makerFeeRate,
    walletMakerFee
  );

  return (
    <Wrap>
      {state.conditionalTpSlModal && marketSymbol && (
        <TPSLModal
          market={marketSymbol}
          initialStopLossPrice={state.conditionalTpSl?.stopLossPrice}
          initialTakeProfitPrice={state.conditionalTpSl?.takeProfitPrice}
          futureTpSl={{
            initialStopLossType: state.conditionalTpSl?.stopLossPriceType,
            initialTakeProfitType: state.conditionalTpSl?.takeProfitPriceType,
            parentOrderId: undefined,
            parentOrderSide: undefined,
            parentBuyQuantity: calculatedInfo.quantityBuy,
            parentSellQuantity: calculatedInfo.quantitySell,
            parentPriceType: state.triggerEnabled
              ? 'stop'
              : state.orderType === 'market'
              ? 'market'
              : 'limit',
            parentPrice:
              state.orderType === 'limit'
                ? state.price
                : state.triggerEnabled
                ? state.triggerPrice
                : undefined,
            onSaveOverride: async ({ side, takeProfit, stopLoss }) => {
              dispatch({
                type: 'setConditionalTpSl',
                tpsl: {
                  side,
                  stopLossPrice: stopLoss?.triggerPrice,
                  stopLossPriceType: stopLoss?.triggerPrice
                    ? stopLoss?.triggerType
                    : undefined,
                  takeProfitPrice: takeProfit?.triggerPrice,
                  takeProfitPriceType: takeProfit?.triggerPrice
                    ? takeProfit.triggerType
                    : undefined,
                },
              });
            },
          }}
          onClose={() => {
            dispatch({ type: 'showConditionlTpSlModal', open: false });
          }}
        />
      )}

      <InnerWrapper
        $minHeight={getOrderbookSectionHeight(verticalResolutionIndex)}
      >
        <div>
          {isMobile ? (
            <MobileLeverageAndTypeBlock>
              <LeverageFieldsWithModal
                marketInitialMarginFractionOverride={
                  initialMarginFractionOverride
                }
                dispatch={dispatch}
                state={state}
                marketData={marketData}
              />
              <div style={{ flex: 1 }}>
                <PanelHeader
                  selectedType={state.orderType}
                  limitOnly={state.limitMakerOnlyMarket}
                  acceptingInput={state.state === 'input'}
                  triggerEnabled={state.triggerEnabled}
                  onChangeType={type =>
                    dispatch({ type: 'changeType', orderType: type })
                  }
                />
              </div>
            </MobileLeverageAndTypeBlock>
          ) : (
            <LeverageFieldsWithModal
              marketInitialMarginFractionOverride={
                initialMarginFractionOverride
              }
              dispatch={dispatch}
              state={state}
              marketData={marketData}
            />
          )}

          {marketData?.status === MarketStatus.limitMakerOnly && (
            <BuyPanelTopWarning isPostOnly />
          )}

          {highSpreadConfirm && (
            <HighSpreadWarningModal
              spreadPercentage={highSpreadConfirm.spread}
              onClose={() => setHighSpreadConfirm(null)}
              onContinue={() => handlePlaceOrder(highSpreadConfirm.side, true)}
            />
          )}

          <LoadingOverlay $isVisible={isLoading} />

          {!isMobile && (
            <PanelHeader
              selectedType={state.orderType}
              limitOnly={state.limitMakerOnlyMarket}
              acceptingInput={state.state === 'input'}
              triggerEnabled={state.triggerEnabled}
              onChangeType={type =>
                dispatch({ type: 'changeType', orderType: type })
              }
            />
          )}
          {/* Only for validation ??? TODO: works ? */}
          <form onSubmit={handleSubmitCheck} ref={formRef}>
            <BuyFields
              isDisabled={isRestrictedRegion}
              token2quote={token2quote}
              token1base={token1base}
              panelState={state}
              panelDispatch={dispatch}
              getBestPrice={getBestPrice}
              priceDecimals={priceDecimals}
              quantityDecimals={quantityDecimals}
              quantityBelowMinimum={quantityBelowMinimum}
              hasDustPosition={hasDustPosition}
              marketData={marketData}
              calculatedInfo={calculatedInfo}
              buyingPower={buyingPower}
            />
          </form>

          {state.errorMessage && (
            <ErrorPanel marginTop={16} message={state.errorMessage} />
          )}

          {/* TODO: remove ? */}
          <div style={{ padding: isMobile ? 4 : 8 }} />
        </div>
        <div>
          {isMobile ? (
            <>
              {(!state.conditionalTpSl ||
                state.conditionalTpSl.side === 'long') && (
                <>
                  <BuySellPanelButton
                    onPlaceOrder={handlePlaceOrder}
                    buyEnabled={buyEnabled}
                    sellEnabled={sellEnabled}
                    panelState={state.state}
                    singleAction="buy"
                  />
                  <div style={{ padding: '4px 0 8px' }}>
                    <MobilePanelInfo
                      calculatedInfo={calculatedInfo}
                      side="buy"
                      token1base={token1base}
                      token2quote={token2quote}
                    />
                  </div>
                </>
              )}
              {(!state.conditionalTpSl ||
                state.conditionalTpSl?.side === 'short') && (
                <>
                  <BuySellPanelButton
                    onPlaceOrder={handlePlaceOrder}
                    buyEnabled={buyEnabled}
                    sellEnabled={sellEnabled}
                    panelState={state.state}
                    singleAction="sell"
                  />
                  <div style={{ padding: '4px 0 4px' }}>
                    <MobilePanelInfo
                      calculatedInfo={calculatedInfo}
                      side="sell"
                      token1base={token1base}
                      token2quote={token2quote}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <BuySellPanelButton
              // This will not be "submit" button,
              // to prevent submitting order by hitting Enter in input fields
              onPlaceOrder={handlePlaceOrder}
              buyEnabled={buyEnabled}
              sellEnabled={sellEnabled}
              panelState={state.state}
              singleAction={
                state.conditionalTpSl?.side
                  ? state.conditionalTpSl.side === 'long'
                    ? 'buy'
                    : 'sell'
                  : undefined
              }
            />
          )}
          {isMobile ? (
            <TakerMakerFeesTwoColumnData
              makerFee={makerFee}
              takerFee={takerFee}
            />
          ) : (
            <>
              <div style={{ padding: 4 }} />
              <BuySellPanelInfoBottom
                calculatedInfo={calculatedInfo}
                makerFee={makerFee}
                takerFee={takerFee}
              />
            </>
          )}
        </div>
      </InnerWrapper>

      {!isMobile && (
        <FooterWrapper>
          <div
            style={{
              padding: '8px 8px 0 8px',
            }}
          >
            <BalanceStatsPanelWithData />
            {ownVault && <VaultStatsPanel managerWallet={ownVault.manager} />}
          </div>
          <BalanceActionsWrap>
            <DepositWithdrawalButtons underEachOther />
          </BalanceActionsWrap>

          {/* <div style={{ height: 32 }} />
          <BalanceActionsWrap>
            <DepositWithdrawalButtons />
          </BalanceActionsWrap>
          <div style={{ padding: '0 12px 12px' }}>
            <BalanceStatsPanel />
          </div> */}
        </FooterWrapper>
      )}
    </Wrap>
  );
}

export default BuySellPanel;
